import { getPacsAETitle, getDicomWebProxyURL, getAppAuthToken } from '../constant';

/**
 * Study volumes read straight from the PACS by the browser, via DICOMweb QIDO.
 *
 * WHY NOT THE BACKEND FIGURE
 * The Super Admin "studies received" numbers came from the `study_count` document, which
 * is a side effect of the CONVERSION PIPELINE rather than a record of what the archive
 * holds. A study only reaches that document if it converted to JPEG/WebM, uploaded to S3
 * and had its filename parsed. Three defects have already been traced to that path: AE
 * titles containing an underscore filed studies under machine names that do not exist; an
 * AE title ending in an underscore had its instances rejected outright; and the writer
 * read-modify-writes a single ~450 KB document, so two conversions finishing together
 * lose one of the two. The archive knows what actually arrived.
 *
 * This talks to the PACS the same way the rest of the app already does — `getPacsURL()`
 * plus the `pacs_access_token` the session holds — so there is no new server dependency,
 * no second auth path, and the request is scoped by the viewer's own credentials.
 */


function pacsToken(): string | null {
  // The proxy authenticates with the app login token, not the shared PACS token.
  return getAppAuthToken() || null;
}

/** DICOM date literal: YYYYMMDD. */
export function dicomDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/**
 * Studies whose StudyDate falls in [from, to].
 *
 * Returns null — never 0 — when the archive could not answer. "No studies arrived that
 * month" is a clinical and commercial statement, and these figures are billed on, so an
 * unreachable archive is never permitted to make it. Callers decide what to show for null.
 *
 * `aeTitle` scopes the count to one sending machine. Note this is as far as attribution
 * can go: QIDO filters on DICOM attributes and "which clinic" is not one, so where two
 * clinics share an AE title (Springwood and Yarrabilba do) their studies cannot be
 * separated here either.
 */
export async function countStudiesBetween(
  from: Date,
  to: Date,
  aeTitle?: string
): Promise<number | null> {
  const base = getDicomWebProxyURL();
  const token = pacsToken();
  if (!base || !token) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('StudyDate', `${dicomDate(from)}-${dicomDate(to)}`);
  if (aeTitle) {
    params.set('ReceivingApplicationEntityTitleOfSeries', aeTitle);
  }

  try {
    const res = await fetch(
      `${base}/rs/studies/count?${params.toString()}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      }
    );
    if (!res.ok) {
      console.warn(`[pacs-studies] count failed (${res.status}) for ${params.get('StudyDate')}`);
      return null;
    }
    const body = await res.json();
    const n = body?.count;
    return typeof n === 'number' ? n : null;
  } catch (err) {
    console.warn('[pacs-studies] count request failed:', err);
    return null;
  }
}

/**
 * Twelve monthly totals for a calendar year, index 0 = January.
 *
 * A month the archive cannot answer stays null so the caller can fall back for that month
 * alone; if every month fails the archive is unreachable, and the whole series is null
 * rather than twelve zeros.
 */
export async function monthlyStudyCounts(
  year: number,
  aeTitle?: string
): Promise<(number | null)[] | null> {
  if (!getDicomWebProxyURL() || !pacsToken()) {
    return null;
  }
  const counts = await Promise.all(
    Array.from({ length: 12 }, (_, i) =>
      countStudiesBetween(new Date(year, i, 1), new Date(year, i + 1, 0, 23, 59, 59, 999), aeTitle)
    )
  );
  return counts.every(v => v === null) ? null : counts;
}

/** The AE title configured for the signed-in session, when the caller wants it scoped. */
export function sessionAeTitle(): string | undefined {
  return getPacsAETitle() || undefined;
}

/**
 * Studies per sending machine (AE title) for one period.
 *
 * Answers the per-clinic question as far as DICOM allows. A clinic's row is the sum of
 * its AE titles; where two clinics share a title the archive cannot separate them either,
 * so the caller keeps flagging those rows as approximate exactly as it does today.
 *
 * Titles are de-duplicated before querying — several clinics can list the same one, and
 * the archive should be asked once per title, not once per clinic.
 *
 * A title the archive could not answer maps to null so the caller can fall back to the
 * backend figure for that clinic alone rather than showing it as zero.
 */
export async function countStudiesByAeTitle(
  aeTitles: string[],
  from: Date,
  to: Date
): Promise<Record<string, number | null>> {
  const unique = [...new Set((aeTitles || []).map(t => (t || '').trim()).filter(Boolean))];
  if (!unique.length) {
    return {};
  }
  const counts = await Promise.all(unique.map(aet => countStudiesBetween(from, to, aet)));
  return unique.reduce<Record<string, number | null>>((acc, aet, i) => {
    acc[aet] = counts[i];
    return acc;
  }, {});
}
